import React, { useState, useEffect, useCallback, memo } from "react";
import { Select, Button, Table, Modal, Form, Input, message, Space, Typography, Tooltip, Tag, Row, Col, Spin, Alert } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { AnimatedStep, BRAND, StepIndicator } from "../hrUsers/staffModalShared";
import {
  getStatesByRole,
  getCleaningAuditRoleId,
  getFacilitiesByState,
  getQuestionsByState,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  getObservationQuestions,
  addObservationQuestion,
  updateObservationQuestion,
  deleteObservationQuestion,
  getAreaRiskTypes,
  getQuestionCategories,
  getQuestionConfig,
  addQuestionConfig,
  updateQuestionConfig,
  getQuestionWhereBank,
  addQuestionWhere,
  updateQuestionWhere,
  deleteQuestionWhere,
  getQuestionWhyBank,
  addQuestionWhy,
  updateQuestionWhy,
  deleteQuestionWhy,
} from "./cleaningAuditApi";

const { Option } = Select;
const { Text } = Typography;
const { confirm } = Modal;

// One screen, three lists:
//  - State Questions (default): managed_questions with facility_id "0".
//  - Facility Questions (withFacility): the same endpoint with a real
//    facility_id. The API answers with the facility's own questions plus the
//    state's, so only rows belonging to the chosen facility are shown here;
//    facility questions are optional, so this list is often empty.
//  - Observation Questions (observation): a separate list with its own
//    endpoints (managed_observation_ques etc.), also optional. It has no
//    configuration or Where/Why comment banks.
//
// Adding or editing a question opens one modal with everything about it, in
// three steps: the question and help text, its category for each area risk
// type (Configure) and its Where and Why comments, saved together.

const listOf = (res) => (!res.error && Array.isArray(res.result?.result) ? res.result.result : []);
const ok = (res) => !res.error && res.result?.success !== false;

// Whether a question's Configure (a category for every area risk type) and
// Where & Why (at least one Where and one Why comment) are filled in.
const checkQuestion = async (stateId, questionId, riskTypes) => {
  const [configRes, whereRes, whyRes] = await Promise.all([
    getQuestionConfig(questionId),
    getQuestionWhereBank(stateId, questionId),
    getQuestionWhyBank(stateId, questionId),
  ]);
  const configured = new Set(
    listOf(configRes)
      .filter((row) => row.que_cat_id)
      .map((row) => String(row.area_risk_type_id)),
  );
  return {
    configOk: riskTypes.length > 0 && riskTypes.every((type) => configured.has(String(type.id))),
    whereWhyOk: listOf(whereRes).length > 0 && listOf(whyRes).length > 0,
  };
};

// Where or Why comments: one text box each, add and remove rows
const CommentList = ({ name, label, placeholder }) => (
  <Form.List
    name={name}
    rules={[
      {
        validator: async (_, rows) => {
          if (!(rows || []).some((r) => r && r.comment && r.comment.trim())) throw new Error(`Please add at least one ${label} comment.`);
        },
      },
    ]}
  >
    {(fields, { add, remove }, { errors }) => (
      <>
        {fields.map(({ key, name: index, ...rest }) => (
          <div key={key} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <Form.Item
              {...rest}
              name={[index, "comment"]}
              style={{ flex: 1, marginBottom: 8 }}
              rules={[{ required: true, whitespace: true, message: "Please enter the comment or remove this row." }]}
            >
              <Input placeholder={placeholder} maxLength={500} />
            </Form.Item>
            <Tooltip title="Remove">
              <Button danger icon={<MinusCircleOutlined />} aria-label="Remove" onClick={() => remove(index)} />
            </Tooltip>
          </div>
        ))}
        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ comment: "" })}>
          Add {label} Comment
        </Button>
        <Form.ErrorList errors={errors} />
      </>
    )}
  </Form.List>
);

const QUESTION_STEPS = [
  { num: 1, label: "Question" },
  { num: 2, label: "Configure" },
  { num: 3, label: "Where & Why" },
];

// The add / edit modal, in the same three-step style as Workforce > Add Staff:
// 1. question and help text, 2. its category for each area risk type,
// 3. its Where and Why comments; one Save on the last step saves them all.
// Observation questions have only the first step.
const QuestionModal = ({ editing, stateId, stateName, facilityId, facilityName, observation, onClose, onSaved }) => {
  const [step, setStep] = useState(1);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!observation);
  const [saving, setSaving] = useState(false);
  const [riskTypes, setRiskTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [question, setQuestion] = useState(editing); // set once a new question is created
  const [savedConfig, setSavedConfig] = useState(new Set()); // risk types that already have a row
  const [savedComments, setSavedComments] = useState({ where: [], why: [] }); // { id, comment } as stored

  useEffect(() => {
    form.setFieldsValue({ que_text: editing?.que_text || "", que_info: editing?.que_info || "", config: {}, where: [{ comment: "" }], why: [{ comment: "" }] });
    if (observation) return undefined;
    let cancelled = false;
    (async () => {
      const [riskRes, catRes, configRes, whereRes, whyRes] = await Promise.all([
        getAreaRiskTypes(stateId),
        getQuestionCategories(),
        editing ? getQuestionConfig(editing.id) : Promise.resolve({ error: false, result: { result: [] } }),
        editing ? getQuestionWhereBank(stateId, editing.id) : Promise.resolve({ error: false, result: { result: [] } }),
        editing ? getQuestionWhyBank(stateId, editing.id) : Promise.resolve({ error: false, result: { result: [] } }),
      ]);
      if (cancelled) return;
      const config = {};
      listOf(configRes).forEach((row) => {
        if (row.que_cat_id) config[row.area_risk_type_id] = row.que_cat_id;
      });
      const where = listOf(whereRes).map((r) => ({ id: r.id, comment: r.where_comment }));
      const why = listOf(whyRes).map((r) => ({ id: r.id, comment: r.why_comment }));
      setRiskTypes(listOf(riskRes));
      setCategories(listOf(catRes));
      setSavedConfig(new Set(listOf(configRes).map((row) => String(row.area_risk_type_id))));
      setSavedComments({ where, why });
      form.setFieldsValue({ config, where: where.length ? where : [{ comment: "" }], why: why.length ? why : [{ comment: "" }] });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, stateId, observation, form]);

  // add new comments, update changed ones, delete removed ones
  const saveComments = async (questionId, kind, rows) => {
    const isWhere = kind === "where";
    const field = isWhere ? "where_comment" : "why_comment";
    const add = isWhere ? addQuestionWhere : addQuestionWhy;
    const update = isWhere ? updateQuestionWhere : updateQuestionWhy;
    const remove = isWhere ? deleteQuestionWhere : deleteQuestionWhy;
    const before = savedComments[kind];
    const keep = rows.filter((r) => r.comment && r.comment.trim()).map((r) => ({ ...r, comment: r.comment.trim() }));
    const keptIds = new Set(keep.filter((r) => r.id).map((r) => String(r.id)));
    const results = await Promise.all([
      ...keep.filter((r) => !r.id).map((r) => add({ state_id: stateId, que_id: questionId, [field]: r.comment })),
      ...keep
        .filter((r) => r.id && before.find((b) => String(b.id) === String(r.id) && b.comment !== r.comment))
        .map((r) => update({ id: r.id, state_id: stateId, que_id: questionId, [field]: r.comment })),
      ...before.filter((b) => !keptIds.has(String(b.id))).map((b) => remove(b.id)),
    ]);
    return results.every(ok);
  };

  const handleSave = async () => {
    let values;
    try {
      await form.validateFields(); // this step's fields; the earlier steps were checked by Next
      // all values, including the ids of saved comments (not form fields)
      values = form.getFieldsValue(true);
    } catch (e) {
      message.error("Please complete the highlighted fields.");
      return;
    }
    setSaving(true);
    try {
      // 1. the question (created first when new, so the rest can refer to it)
      const text = { que_text: values.que_text.trim(), que_info: values.que_info.trim() };
      let questionId = question?.id;
      let res;
      if (observation) {
        res = questionId ? await updateObservationQuestion({ id: questionId, ...text }) : await addObservationQuestion({ state_id: stateId, ...text });
      } else {
        // state questions send facility_id "" (the API treats "0" as a facility id)
        const payload = { state_id: stateId, facility_id: facilityId && String(facilityId) !== "0" ? String(facilityId) : "", ...text };
        res = questionId ? await updateQuestion({ ...payload, id: questionId }) : await addQuestion(payload);
      }
      if (!ok(res)) {
        message.error(res.result?.message || "The question could not be saved. Please try again.");
        return;
      }
      if (!questionId) questionId = res.result?.result?.insertId;
      if (observation) {
        message.success(question ? "Question updated successfully." : "Question added successfully.");
        onSaved();
        return;
      }
      if (!questionId) {
        message.error("The question was saved, but its details could not be. Please open it from the list and save again.");
        onSaved();
        return;
      }
      // a retry after a partial failure updates this question instead of adding another
      if (!question) setQuestion({ id: questionId, ...text });

      // 2. Configure: a category for each area risk type
      const configResults = await Promise.all(
        riskTypes.map(async (type) => {
          const body = { que_id: questionId, area_risk_type_id: type.id, que_cat_id: values.config[type.id] };
          const r = savedConfig.has(String(type.id)) ? await updateQuestionConfig(body) : await addQuestionConfig(body);
          return { typeId: String(type.id), ok: ok(r) };
        }),
      );
      setSavedConfig((prev) => new Set([...prev, ...configResults.filter((r) => r.ok).map((r) => r.typeId)]));

      // 3. Where and Why comments
      const whereOk = await saveComments(questionId, "where", values.where || []);
      const whyOk = await saveComments(questionId, "why", values.why || []);

      const failed = [configResults.some((r) => !r.ok) && "Configure", !whereOk && "Where comments", !whyOk && "Why comments"].filter(Boolean);
      if (failed.length) {
        message.error(`The question was saved, but these could not be: ${failed.join(", ")}. Please open it from the list and save again.`);
        onSaved();
        return;
      }
      message.success(question ? "Question updated successfully." : "Question added successfully.");
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const steps = observation ? QUESTION_STEPS.slice(0, 1) : QUESTION_STEPS;
  const isLast = step === steps.length;
  const place = [stateName, facilityName].filter(Boolean).join(" · ");

  // Next checks this step's fields before moving on
  const goNext = async () => {
    try {
      await form.validateFields();
      setStep((s) => Math.min(s + 1, steps.length));
    } catch (e) {
      message.error("Please complete the highlighted fields.");
    }
  };

  const questionStep = (
    <>
      <Form.Item name="que_text" label="Question Text" rules={[{ required: true, whitespace: true, message: "Please enter the question text." }]}>
        <Input.TextArea rows={3} maxLength={500} placeholder="e.g. Are the bathroom surfaces clean and free of dust?" />
      </Form.Item>
      <Form.Item name="que_info" label="Help Text" rules={[{ required: true, whitespace: true, message: "Please enter the help text." }]}>
        <Input.TextArea rows={3} maxLength={500} placeholder="What the auditor should check" />
      </Form.Item>
    </>
  );

  const configureStep = (
    <>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        The question&apos;s category for each area risk type. Audit scores use this weighting.
      </Text>
      {!loading && riskTypes.length === 0 ? (
        <Alert type="warning" showIcon message="No area risk types are set up for this state yet, so the question cannot be configured." />
      ) : (
        <Row gutter={16}>
          {riskTypes.map((type) => (
            <Col xs={24} sm={12} key={type.id}>
              <Form.Item name={["config", String(type.id)]} label={type.name} rules={[{ required: true, message: "Please select a category." }]}>
                <Select placeholder="Select a category">
                  {categories.map((cat) => (
                    <Option key={cat.id} value={cat.id}>
                      {cat.risk_cat}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          ))}
        </Row>
      )}
    </>
  );

  const whereWhyStep = (
    <>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        The comments an auditor picks from when the answer is not met: where the problem was, and why.
      </Text>
      <Row gutter={24}>
        <Col xs={24} md={12}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Where Comments</div>
          <CommentList name="where" label="Where" placeholder="e.g. Shower recess" />
        </Col>
        <Col xs={24} md={12}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Why Comments</div>
          <CommentList name="why" label="Why" placeholder="e.g. Soap scum build-up" />
        </Col>
      </Row>
    </>
  );

  return (
    <Modal
      visible
      onCancel={onClose}
      width={750}
      maskClosable={false}
      destroyOnClose
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>{editing ? "Edit Question" : "Add Question"}</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: "#5B6472", marginTop: 2 }}>
            {observation ? "The question and its help text." : "The question, its category for each area risk type, and its Where and Why comments."}
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text style={{ fontSize: 12, color: "#5B6472" }}>
            Step {step} of {steps.length} — {steps[step - 1].label}
          </Text>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose}>Cancel</Button>
            {step > 1 && (
              <Button icon={<ArrowLeftOutlined />} onClick={() => setStep((s) => s - 1)}>
                Back
              </Button>
            )}
            {isLast ? (
              <Button
                type="primary"
                style={{ background: BRAND, borderColor: BRAND }}
                icon={<SaveOutlined />}
                loading={saving}
                disabled={loading}
                onClick={handleSave}
              >
                Save Question
              </Button>
            ) : (
              <Button type="primary" style={{ background: BRAND, borderColor: BRAND }} icon={<ArrowRightOutlined />} disabled={loading} onClick={goNext}>
                Next
              </Button>
            )}
          </div>
        </div>
      }
      bodyStyle={{ padding: "16px 20px", minHeight: 280 }}
    >
      <div
        style={{
          background: "#e8f0f9",
          borderRadius: 6,
          padding: "10px 16px",
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text style={{ fontSize: 15, fontWeight: 700, color: BRAND }} ellipsis>
          {editing?.que_text || "New question"}
        </Text>
        <Text style={{ fontSize: 12, color: "#5B6472", whiteSpace: "nowrap" }}>{place || (editing ? "Edit" : "New")}</Text>
      </div>
      {steps.length > 1 && <StepIndicator currentStep={step} steps={steps} />}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <AnimatedStep stepKey={step}>{step === 1 ? questionStep : step === 2 ? configureStep : whereWhyStep}</AnimatedStep>
        </Form>
      </Spin>
    </Modal>
  );
};

// Read-only view of a question: the question, its category for each area
// risk type and its Where and Why comments (as Workforce > View Staff)
const Section = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: BRAND, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>{title}</div>
    {children}
  </div>
);

const QuestionViewModal = ({ question, stateId, stateName, facilityName, observation, onClose, onEdit }) => {
  const [loading, setLoading] = useState(!observation);
  const [config, setConfig] = useState([]); // { type, category }
  const [where, setWhere] = useState([]);
  const [why, setWhy] = useState([]);

  useEffect(() => {
    if (observation) return undefined;
    let cancelled = false;
    (async () => {
      const [riskRes, catRes, configRes, whereRes, whyRes] = await Promise.all([
        getAreaRiskTypes(stateId),
        getQuestionCategories(),
        getQuestionConfig(question.id),
        getQuestionWhereBank(stateId, question.id),
        getQuestionWhyBank(stateId, question.id),
      ]);
      if (cancelled) return;
      const catName = {};
      listOf(catRes).forEach((c) => {
        catName[String(c.id)] = c.risk_cat;
      });
      const chosen = {};
      listOf(configRes).forEach((row) => {
        if (row.que_cat_id) chosen[String(row.area_risk_type_id)] = catName[String(row.que_cat_id)] || "-";
      });
      setConfig(listOf(riskRes).map((type) => ({ id: type.id, type: type.name, category: chosen[String(type.id)] || null })));
      setWhere(listOf(whereRes).map((r) => r.where_comment));
      setWhy(listOf(whyRes).map((r) => r.why_comment));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [question, stateId, observation]);

  const place = [stateName, facilityName].filter(Boolean).join(" · ");
  const comments = (rows, label) =>
    rows.length ? (
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {rows.map((c, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            {c}
          </li>
        ))}
      </ul>
    ) : (
      <Tag color="red">No {label} comments yet</Tag>
    );

  return (
    <Modal
      visible
      onCancel={onClose}
      width={750}
      destroyOnClose
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>View Question</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: "#5B6472", marginTop: 2 }}>Everything this question uses in an audit.</div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>Close</Button>
          <Button type="primary" style={{ background: BRAND, borderColor: BRAND }} icon={<EditOutlined />} onClick={onEdit}>
            Edit
          </Button>
        </div>
      }
      bodyStyle={{ padding: "16px 20px", minHeight: 240 }}
    >
      <div
        style={{
          background: "#e8f0f9",
          borderRadius: 6,
          padding: "10px 16px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text style={{ fontSize: 15, fontWeight: 700, color: BRAND }} ellipsis>
          {question.que_text}
        </Text>
        <Text style={{ fontSize: 12, color: "#5B6472", whiteSpace: "nowrap" }}>{place}</Text>
      </div>
      <Section title="Question">
        <div style={{ marginBottom: 6 }}>
          <Text type="secondary">Question Text</Text>
          <div>{question.que_text}</div>
        </div>
        <div>
          <Text type="secondary">Help Text</Text>
          <div>{question.que_info || "-"}</div>
        </div>
      </Section>
      {!observation && (
        <Spin spinning={loading}>
          <Section title="Configure">
            {!loading && config.length === 0 ? (
              <Text type="secondary">No area risk types are set up for this state yet.</Text>
            ) : (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={config}
                columns={[
                  { title: "Area Risk Type", dataIndex: "type", key: "type" },
                  { title: "Category", dataIndex: "category", key: "category", render: (v) => v || <Tag color="red">Not set</Tag> },
                ]}
              />
            )}
          </Section>
          <Section title="Where & Why">
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Where Comments</div>
                {comments(where, "Where")}
              </Col>
              <Col xs={24} md={12}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Why Comments</div>
                {comments(why, "Why")}
              </Col>
            </Row>
          </Section>
        </Spin>
      )}
    </Modal>
  );
};

const Questions = ({ withFacility = false, observation = false }) => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(null); // { editing } while the add / edit modal is open
  const [viewing, setViewing] = useState(null); // the question shown read-only
  const [completeness, setCompleteness] = useState({}); // question id -> { configOk, whereWhyOk }

  useEffect(() => {
    (async () => {
      try {
        const roleId = await getCleaningAuditRoleId();
        const { error, result } = await getStatesByRole(roleId);
        setStates(!error && Array.isArray(result?.result) ? result.result : []);
      } catch (e) {
        setStates([]);
        message.error({ key: "cleaning-audit-connect", content: "Couldn't connect to Cleaning Audit. Please try again shortly." });
      }
    })();
  }, []);

  const onStateChange = useCallback(
    async (value) => {
      setStateId(value);
      setFacilityId(null);
      setFacilities([]);
      if (!withFacility || !value) return;
      const { error, result } = await getFacilitiesByState(value);
      setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
    },
    [withFacility],
  );

  const loadQuestions = useCallback(async () => {
    if (!stateId || (withFacility && !facilityId)) {
      message.error(withFacility ? "Please select a state and facility first." : "Please select a state first.");
      return;
    }
    setLoading(true);
    setLoaded(true);
    const { error, result } = observation ? await getObservationQuestions(stateId) : await getQuestionsByState(stateId, withFacility ? facilityId : "0");
    const list = !error && Array.isArray(result?.result) ? result.result : [];
    // The facility request also returns the state's questions (facility_id 0).
    const shown = withFacility ? list.filter((q) => String(q.facility_id) === String(facilityId)) : list;
    setRows(shown);
    setLoading(false);

    // which questions still miss their Configure or Where & Why
    setCompleteness({});
    if (observation || !shown.length) return;
    const types = listOf(await getAreaRiskTypes(stateId));
    const checked = await Promise.all(shown.map(async (q) => [q.id, await checkQuestion(stateId, q.id, types)]));
    setCompleteness(Object.fromEntries(checked));
  }, [stateId, facilityId, withFacility, observation]);

  const handleDelete = useCallback(
    (row) => {
      confirm({
        title: "Delete question",
        content: observation ? `Delete "${row.que_text}"?` : `Delete "${row.que_text}"? This also removes its Where and Why comments.`,
        okText: "Delete",
        okType: "danger",
        onOk: async () => {
          const { error, result } = observation ? await deleteObservationQuestion(row.id) : await deleteQuestion(row.id);
          if (error) message.error(result?.message || "The question could not be deleted. Please try again.");
          else {
            message.success("Question deleted successfully.");
            loadQuestions();
          }
        },
      });
    },
    [loadQuestions, observation],
  );

  const statusTag = (row) => {
    const status = completeness[row.id];
    if (!status) return <Text type="secondary">…</Text>;
    const missing = [!status.configOk && "Configure", !status.whereWhyOk && "Where & Why"].filter(Boolean);
    return missing.length ? <Tag color="red">Missing: {missing.join(", ")}</Tag> : <Tag color="green">Complete</Tag>;
  };

  const columns = [
    { title: "Question Text", dataIndex: "que_text", key: "que_text" },
    { title: "Help Text", dataIndex: "que_info", key: "que_info" },
    ...(observation ? [] : [{ title: "Setup", key: "setup", width: 220, render: (_, row) => statusTag(row) }]),
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, row) => (
        <Space>
          <Tooltip title="View">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setViewing(row)} aria-label="View" />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => setPage({ editing: row })} type="primary" aria-label="Edit" />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} aria-label="Delete" />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const listTitle = observation ? "Observation Questions" : withFacility ? "Facility Questions" : "State Questions";
  const emptyText = observation
    ? "No observation questions for this state yet. They are optional."
    : withFacility
      ? "No questions specific to this facility yet. They are optional."
      : "No questions for this state yet.";

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select showSearch optionFilterProp="children" placeholder="Select a state" style={{ width: 220 }} value={stateId} onChange={onStateChange}>
              {states.map((s) => (
                <Option key={s.state_id} value={s.state_id}>
                  {s.state_name}
                </Option>
              ))}
            </Select>
          </div>
          {withFacility && (
            <div>
              <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility</div>
              <Select
                showSearch
                optionFilterProp="children"
                placeholder="Select a facility"
                style={{ width: 220 }}
                value={facilityId}
                onChange={setFacilityId}
                disabled={!stateId}
                notFoundContent={stateId ? "No facilities for this state" : "Select a state first"}
              >
                {facilities.map((f) => (
                  <Option key={f.facility_id} value={f.facility_id}>
                    {f.facility_name}
                  </Option>
                ))}
              </Select>
            </div>
          )}
          <Button type="primary" loading={loading} onClick={loadQuestions}>
            Load Questions
          </Button>
          {loaded && (
            <Button icon={<PlusOutlined />} onClick={() => setPage({ editing: null })}>
              Add Question
            </Button>
          )}
        </div>
      </div>

      {viewing && (
        <QuestionViewModal
          question={viewing}
          stateId={stateId}
          stateName={(states.find((st) => st.state_id === stateId) || {}).state_name}
          facilityName={withFacility ? (facilities.find((f) => f.facility_id === facilityId) || {}).facility_name : null}
          observation={observation}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setPage({ editing: viewing });
            setViewing(null);
          }}
        />
      )}

      {page && (
        <QuestionModal
          editing={page.editing}
          stateId={stateId}
          stateName={(states.find((st) => st.state_id === stateId) || {}).state_name}
          facilityId={withFacility ? facilityId : null}
          facilityName={withFacility ? (facilities.find((f) => f.facility_id === facilityId) || {}).facility_name : null}
          observation={observation}
          onClose={() => setPage(null)}
          onSaved={() => {
            setPage(null);
            loadQuestions();
          }}
        />
      )}

      {loaded && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, margin: "8px 0 12px" }}>{listTitle}</div>
          <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} locale={{ emptyText }} />
        </div>
      )}
    </div>
  );
};

export default memo(Questions);
